/**
 * Ayarlar › API kullanımı state (v0.1.164): GET /settings/api-usage every time the screen opens (the last view stays
 * on screen while it loads), again on Yenile and on Tekrar dene after an error. Read only.
 */

import { useSyncExternalStore } from 'react';
import type { ApiUsageView } from '../api/api-usage.js';
import type { TwinApi } from '../api/client.js';
import { useStore } from './context.js';

export interface ApiUsageState {
  view: ApiUsageView | null;
  error: string | null;
  loading: boolean;
}

export class ApiUsageStore {
  private state: ApiUsageState = { view: null, error: null, loading: false };
  private inFlight: Promise<void> | null = null;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly api: TwinApi) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getState = (): ApiUsageState => this.state;

  private set(patch: Partial<ApiUsageState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  /** Reads again, keeping the current view meanwhile; one read at a time. The screen calls it on mount. */
  reload(): Promise<void> {
    if (this.inFlight !== null) return this.inFlight;
    this.set({ loading: true });
    const run = (async () => {
      try {
        this.set({ view: await this.api.getApiUsage(), error: null, loading: false });
      } catch (error) {
        this.set({ error: error instanceof Error ? error.message : String(error), loading: false });
      }
    })();
    this.inFlight = run;
    void run.finally(() => {
      if (this.inFlight === run) this.inFlight = null;
    });
    return run;
  }
}

export function useApiUsage(): { store: ApiUsageStore; state: ApiUsageState } {
  const store = useStore().apiUsage;
  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  return { store, state };
}
